import { DynaVault } from '../../../../api/DynaVaultsAPI';
import { BigNumberish, ethers, BigNumber } from 'ethers';
import { ETH_ZERO_ADDRESS } from 'utils/constants/ethConst';
import { VaultType } from 'structure/pv2/vault/configs/vaultTypes';

export interface VaultIdentity {
  address: `0x${string}`;
  chainId: number;
}

export const riskLevelLabels: Record<DynaVault['riskLevel'], string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  USER_DEFINED: 'User Defined',
};

export const getRiskLevelColorClass = (
  riskLevel: DynaVault['riskLevel']
): string => {
  if (riskLevel === 'LOW') return 'text-[#9DB178]';
  if (riskLevel === 'MEDIUM') return 'text-[#EBEB77]';
  if (riskLevel === 'HIGH') return 'text-[#F28D8B]';
  if (riskLevel === 'USER_DEFINED') return 'text-[#E48422]';
  return 'text-[#9DB178]';
};

export const getRiskLevelLabel = (
  riskLevel: DynaVault['riskLevel']
): string => {
  return riskLevelLabels[riskLevel] || 'Unknown';
};

export const parseVaultId = (vaultId: string): VaultIdentity | null => {
  if (!vaultId) {
    return null;
  }

  const [address, chainId] = vaultId.split(':');

  if (!address || !chainId) {
    return null;
  }
  return { address: address as `0x${string}`, chainId: Number(chainId) };
};

export const getVaultTvl = (vault: DynaVault) => {
  return parseFloat(vault.availableFunds) + parseFloat(vault.deployedFunds);
};

export const getDynaRouterAddress = (vault: DynaVault) =>
  vault?.metadata?.contracts?.VaultZapper || ETH_ZERO_ADDRESS;

export const getVaultZapperAddress = (vault: DynaVault) =>
  vault?.metadata?.contracts?.VaultZapper || ETH_ZERO_ADDRESS;

export const getVaultDepositFee = (vault: DynaVault) => {
  const fee = vault.fees.find((fee) => fee.key === 'deposit-fee');
  return fee ? parseFloat(fee.percentage) : 0;
};

export const getVaultWithdrawalFee = (vault: DynaVault) => {
  const fee = vault.fees.find((fee) => fee.key === 'redemption-fee');
  return fee ? parseFloat(fee.percentage) : 0;
};

export const getVaultManagementFee = (vault: DynaVault) => {
  const fee = vault.fees.find((fee) => fee.key === 'management-fee');
  return fee ? parseFloat(fee.percentage) : 0;
};

export const getVaultPerformanceFee = (vault: DynaVault) => {
  const fee = vault.fees.find((fee) => fee.key === 'performance-fee');
  return fee ? parseFloat(fee.percentage) : 0;
};

export const normalizeBalanceInUSDC = (params: {
  tokenAmount: bigint;
  tokenDecimals: number;
  tokenPrice: number;
}) => {
  const { tokenAmount, tokenDecimals, tokenPrice } = params;

  const normalizedBalance = parseFloat(
    ethers.utils.formatUnits(tokenAmount.toString(), tokenDecimals)
  );

  const usdcAmount = normalizedBalance * tokenPrice;

  if (usdcAmount > 0.01) {
    return usdcAmount;
  }

  return 0;
};

export const calcMinAmountOut = (
  amount: BigNumberish,
  slippage: number // 0.1% | 0.5% | 1% | 5% | 10%
): BigNumber => {
  const bigAmount = BigNumber.from(amount);

  // Convert slippage percentage to basis points (e.g., 0.1% = 10 basis points)
  const slippageBasisPoints = Math.floor(slippage * 100);

  // Basis points scale (10000 basis points = 100%)
  const basisPointsScale = BigNumber.from(10000);

  // Calculate the minimum amount out (amount * (1 - slippage))
  const slippageValue = bigAmount
    .mul(slippageBasisPoints)
    .div(basisPointsScale);
  const minAmountOut = bigAmount.sub(slippageValue);

  return minAmountOut;
};

export const getVaultPerformance = (vault: DynaVault) => {
  return [
    {
      title: '24-hour',
      value: vault.twentyFourHourPerformance,
    },
    {
      title: '7 days',
      value: vault.weeklyPerformance,
    },
    {
      title: '30 days',
      value: vault.monthlyPerformance,
    },
    {
      title: '12 months',
      value: vault.yearlyPerformance,
    },
    {
      title: 'Since inception',
      value: vault.sinceInceptionPerformance,
    },
    {
      title: 'Overall',
      value: vault.netPerformance,
    },
  ];
};

export const getVaultAPYGroup = (vault: DynaVault) => {
  return [
    {
      title: '24-hour',
      value: vault.twentyFourHourAPY,
    },
    {
      title: '7 days',
      value: vault.weeklyAPY,
    },
    {
      title: '30 days',
      value: vault.monthlyAPY,
    },
    {
      title: '12 months',
      value: vault.yearlyAPY,
    },
    {
      title: 'Since inception',
      value: vault.sinceInceptionAPY,
    },
    {
      title: 'Overall',
      value: vault.sinceInceptionAPY,
    },
  ];
};

export const getVaultAPY = (vault: DynaVault) => {
  let apy =
    vault.type === VaultType.YIELD
      ? parseFloat(vault.twentyFourHourAPY.toString())
      : parseFloat(vault.sinceInceptionPerformance);

  if (vault.type === VaultType.YIELD && apy >= 1000) {
    apy = 1000;
  }

  return apy;
};

export const getVaultAPYPrefix = (vault: DynaVault, apy: number) => {
  return !vault.isClosed && vault.type === VaultType.YIELD && apy >= 1000
    ? '+'
    : '';
};

export const getVaultAPYDisplay = (vault: DynaVault) => {
  const value = getVaultAPY(vault);
  const isAvailable = !vault.isClosed && value !== 0;

  return {
    value,
    prefix: isAvailable ? getVaultAPYPrefix(vault, value) : '',
    isAvailable,
  };
};
